import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { SetCurrentSeasons } from '@protector/entities/core/__legacy__/redux.actions';
import type { Property } from '@protector/entities/property/models';
import { ReloadRegion } from '@protector/entities/region/__legacy__/redux.actions';
import type { ReloadRegionData } from '@protector/entities/region/models';
import type { Season } from '@protector/entities/season/models';
import { CropTypesEnum } from '@protector/utils/constants';
import { Set as ImmutableSet } from 'immutable';

import { isActive } from '@protector/entities/season/utils';
import type { UUID } from '@protector/utils/types';
import { Button, type RadioChangeEvent } from 'antd';
import { Divider, Radio } from 'antdV3';
import { COLOR_OPTIONS } from 'config/style/color-options';
import { BreadcrumbSeasonsGroup } from 'core/components/header/components/breadcrumb/breadcrumb-seasons.enum';
import { sortedSeasonsByYear } from 'core/components/header/components/breadcrumb/functions/seasons.functions';
import { getSelectedCompanyId } from 'core/core.selectors';
import STTypo from 'core/shared/typo';
import { TypeOptions } from 'core/shared/typo/typo.models';
import useSegmentTracking from 'core/utils/segment/useSegmentTracking';
import { compareStrings } from 'core/utils/strings';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { getSeasonsGroupedByYear, selectAllSeasons } from 'entities/season/season.reducer';
import type { Dictionary } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import SvgSearchGlyph from 'st-protector-icons/Glyph/SearchGlyph';
import {
  BlueRadioGroup,
  SeasonListContainer,
  SeasonPopoverBody,
  SeasonPopoverFooter,
  SeasonPopoverHeader,
  SeasonPopoverSearch
} from './season-popover.style';

interface Props {
  close: () => void;
  localMode: BreadcrumbSeasonsGroup;
  setLocalMode: (mode: BreadcrumbSeasonsGroup) => void;
  setCurrentSelected: (seasonId: UUID | null) => void;
  currentSelected: UUID | null;
}

export const SeasonPopover = ({ close, localMode, setLocalMode, setCurrentSelected, currentSelected }: Props) => {
  const [t] = useTranslation();

  const segmentTracking = useSegmentTracking();

  const seasonsGroups = useSelector(getSeasonsGroupedByYear);
  const property = useSelector<AppState, Property | undefined>(getSelectedProperty);
  const organizationId = useSelector<AppState, string | null>(state => getSelectedCompanyId(state));

  const seasons = useSelector<AppState, Season[]>(state => selectAllSeasons(state.entities.season));

  const [filteredSeasonGroups, setFilteredSeasonGroups] = useState<Dictionary<Season[]>>({});
  const [filteredSeasons, setFilteredSeasons] = useState<Season[]>([]);
  const [sortedGroupKeys, setSortedGroupKeys] = useState<string[]>([]);

  const [inactiveSortedGroupKeys, setInactiveSortedGroupKeys] = useState<ImmutableSet<UUID>>();
  const [activeSortedGroupKeys, setActiveSortedGroupKeys] = useState<ImmutableSet<UUID>>();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  /**
   * `useLoadRegions` reacts to `selectedSeasons` and dispatches `ReloadRegion` itself, so dispatching
   * it here too would double every `/by-seasons` request. Kept only for an empty selection, since
   * that's the one case `useLoadRegions` skips (it gates on `selectedSeasons.length`).
   */
  const reloadRegionData = useCallback(
    (seasonIds: UUID[]) => {
      dispatch(SetCurrentSeasons(seasonIds));
      if (!seasonIds.length && property) {
        dispatch(ReloadRegion({ regionId: property.root_region_id, seasonIds } as ReloadRegionData));
      }
    },
    [dispatch, property]
  );

  const getFilteredSeasons = useCallback(
    (searchText = '') => {
      const isCotton = pathname?.includes('cotton');
      if (searchText === '' && !isCotton) {
        return { seasons, seasonsGroups };
      }

      const newSeasonGroups: Dictionary<Season[]> = {};
      Object.keys(seasonsGroups).forEach(seasonGroupYear => {
        newSeasonGroups[seasonGroupYear] = seasonsGroups[seasonGroupYear].filter(season => {
          const matchesSearchText = season.name.toLowerCase().includes(searchText.toLowerCase());
          const matchesCottonFilter = isCotton ? season.crop?.wk_slug.toLowerCase() === CropTypesEnum.COTTON.toLowerCase() : true;
          return matchesSearchText && matchesCottonFilter;
        });
      });
      const filteredSeasons = seasons.filter(season => {
        const matchesSearchText = season.name.toLowerCase().includes(searchText.toLowerCase());
        const matchesCottonFilter = isCotton ? season.crop?.wk_slug.toLowerCase() === CropTypesEnum.COTTON.toLowerCase() : true;
        return matchesSearchText && matchesCottonFilter;
      });

      return { seasons: filteredSeasons, seasonsGroups: newSeasonGroups };
    },
    [pathname, seasonsGroups, seasons]
  );

  useEffect(() => {
    if (pathname?.includes('cotton')) {
      const { seasons: newSeasons, seasonsGroups: newSeasonGroups } = getFilteredSeasons();
      setFilteredSeasons(newSeasons);
      setFilteredSeasonGroups(newSeasonGroups);
    } else {
      setFilteredSeasons(seasons);
      setFilteredSeasonGroups(seasonsGroups);
    }
  }, [seasonsGroups, pathname, seasons, getFilteredSeasons]);

  useEffect(() => {
    const tempArray = Object.keys(filteredSeasonGroups).sort(compareStrings).reverse();
    setSortedGroupKeys(tempArray);
  }, [filteredSeasonGroups]);

  useEffect(() => {
    const [tempActiveSeasonsGroups, tempInactiveSeasonsGroups] = sortedSeasonsByYear(sortedGroupKeys, filteredSeasonGroups) as [
      UUID[],
      UUID[]
    ];
    setActiveSortedGroupKeys(ImmutableSet(tempActiveSeasonsGroups));
    setInactiveSortedGroupKeys(ImmutableSet(tempInactiveSeasonsGroups));
  }, [sortedGroupKeys, filteredSeasonGroups]);

  const handleCancel = () => {
    close();
  };

  const handleSave = () => {
    segmentTracking.track('Season Selector - Changed season filter', { selectedSeasonId: currentSelected ?? undefined });
    const activeSeasonsArray = filteredSeasons.filter(seas => isActive(seas));
    const activeSeasons = activeSeasonsArray.map(season => season.id);
    close();
    reloadRegionData(currentSelected ? [currentSelected] : activeSeasons);
  };

  const handleModeRadioGroupChange = (value: RadioChangeEvent) => {
    setLocalMode(value.target.value as BreadcrumbSeasonsGroup);
    if (value.target.value === BreadcrumbSeasonsGroup.ALL_ACTIVES) {
      setCurrentSelected(null);
    }
  };

  const handleCheckChange = (eventSeason: RadioChangeEvent) => {
    setCurrentSelected((eventSeason?.target.value as UUID) || null);
    setLocalMode(BreadcrumbSeasonsGroup.BY_SEASON);
  };

  const onSelectInactive = (val: RadioChangeEvent) => {
    setCurrentSelected(val.target.value as UUID);
    setLocalMode(BreadcrumbSeasonsGroup.BY_SEASON);
  };

  const removeDuplicatedSeasons = (allSeasons: Season[]): Season[] => {
    return ImmutableSet(allSeasons).toArray();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase();

    if (searchTerm === '') {
      setFilteredSeasonGroups(seasonsGroups);
      setFilteredSeasons(seasons);
      return;
    }
    const { seasons: newSeasons, seasonsGroups: newSeasonGroups } = getFilteredSeasons(searchTerm);

    setFilteredSeasonGroups(newSeasonGroups);
    setFilteredSeasons(newSeasons);
  };

  return (
    <SeasonPopoverBody>
      <SeasonPopoverHeader>
        <SeasonPopoverSearch onChange={handleSearchChange} suffix={<SvgSearchGlyph />} />
      </SeasonPopoverHeader>
      <div>
        <BlueRadioGroup value={localMode} onChange={handleModeRadioGroupChange} className='grid'>
          <Radio value={BreadcrumbSeasonsGroup.ALL_ACTIVES} className='py-1.5 px-3' data-testid='season-select-all-active'>
            {t('components.breadcrumb.all_active_seasons')}
          </Radio>
        </BlueRadioGroup>
        <Divider style={{ margin: '16px 0 8px 0 ' }} />
      </div>
      <SeasonListContainer>
        <STTypo fontFamily='Noto Sans' color={COLOR_OPTIONS.SECONDARY} type={TypeOptions.P2} fontWeight={600}>
          {t('general.context_select.season_select.actives')}
        </STTypo>
        <BlueRadioGroup onChange={handleCheckChange} value={currentSelected}>
          {activeSortedGroupKeys && activeSortedGroupKeys.toArray().length > 0 && (
            <div>
              {activeSortedGroupKeys.map((year, idx) => {
                return (
                  <div key={`${year}-${idx}`}>
                    <p className='st-seasons--section-title'>
                      <STTypo fontFamily='Noto Sans' color={COLOR_OPTIONS.SECONDARY} type={TypeOptions.P2} fontWeight={600}>
                        {`${t('general.map.multilayer.nemadigital.season')} ${year}`}
                      </STTypo>
                    </p>

                    <div data-testid='seasons-group'>
                      {seasonsGroups[year] &&
                        removeDuplicatedSeasons(seasonsGroups[year]).map((season, seasonIdx) => (
                          <Fragment key={idx + seasonIdx}>
                            {isActive(season) && (
                              <div key={idx + seasonIdx} data-testid='season-select-active'>
                                <Radio value={season.id} data-testid={`season-select-active-${seasonIdx}`}>
                                  <STTypo nowrap maxlength={50}>
                                    {season.name}
                                  </STTypo>
                                </Radio>
                              </div>
                            )}
                          </Fragment>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </BlueRadioGroup>
        <BlueRadioGroup onChange={onSelectInactive} value={currentSelected}>
          <p className='st-seasons--section-title'>
            <STTypo fontFamily='Noto Sans' color={COLOR_OPTIONS.SECONDARY} type={TypeOptions.P2} fontWeight={600}>
              {t('general.context_select.season_select.inactives_seasons')}
            </STTypo>
          </p>
          {inactiveSortedGroupKeys && inactiveSortedGroupKeys.toArray().length > 0 ? (
            <>
              {inactiveSortedGroupKeys.map(
                (year, idx) =>
                  seasonsGroups[year] &&
                  removeDuplicatedSeasons(seasonsGroups[year]).map((season, seasonIdx) => (
                    <Fragment key={idx + seasonIdx}>
                      {!isActive(season) && (
                        <div key={idx + seasonIdx} data-testid='season-select-inactive'>
                          <Radio value={season.id} data-testid={`season-select-inactive-${seasonIdx}`}>
                            <STTypo nowrap maxlength={50}>
                              {season.name}
                            </STTypo>
                          </Radio>
                        </div>
                      )}
                    </Fragment>
                  ))
              )}
            </>
          ) : (
            <STTypo fontFamily='Noto Sans' fontWeight={600} color={COLOR_OPTIONS.PRIMARY} type={TypeOptions.P2}>
              {t('general.context_select.season_select.no_inactive_seasons')}
            </STTypo>
          )}
        </BlueRadioGroup>
      </SeasonListContainer>
      <Divider style={{ margin: '12px 0' }} />

      <SeasonPopoverFooter>
        <a
          target='_blank'
          rel='noreferrer'
          href={`${import.meta.env.VITE_CROPWISE_URL}/landing-organization/${organizationId}/property/${property?.id}/table-view`}>
          <STTypo fontFamily='Noto Sans' color={COLOR_OPTIONS.BLUE_60}>
            {t('components.breadcrumb.manage_seasons')}
          </STTypo>
        </a>
        <SeasonPopoverFooter>
          <Button type='text' onClick={handleCancel}>
            {t('general.cancel')}
          </Button>
          <Button type='primary' onClick={handleSave} data-testid='season-selector-apply'>
            {t('general.apply')}
          </Button>
        </SeasonPopoverFooter>
      </SeasonPopoverFooter>
    </SeasonPopoverBody>
  );
};
